/* eslint-disable no-param-reassign */
import { useState, useEffect, useCallback } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Grid, Paper, Stack } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { SelectionForm } from 'src/modules/groupMessageEmail/components/selectionForm';
import { Selection } from 'src/modules/groupMessageEmail/models/selection';

import { getSelectionById } from 'src/shared/services/groupMessageService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { LoadingButton } from '@mui/lab';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { getGroupMessageSmsById, updateGroupMessageSms } from 'src/shared/services/groupMessageSmsService';
import { RegexHelper } from 'src/shared/helpers/regexHelper';
import { FixedSelection } from '../models/fixedSelection';
import { GroupMessageSmsResponse } from '../models/groupMessageSmsRespons';
import { GroupMessageSmsRequestPut } from '../models/groupMessageSmsRequestPut';
import { GroupMessageSmsMessageLength } from '../components/groupMessageSmsMessageLength';
import { GroupMessageSmsPreview } from '../components/groupMessageSmsPreview';
import { GroupMessageSmsStatusMessage } from '../components/groupMessageSmsStatusMessage';

const GroupMessageSmsEditPage = () => {
  const { t } = useTranslation(['groupMessage', 'common']);
  const params = useParams();
  const navigate = useNavigate();

  const splitMessageLength = 160; // Number of characters for each SMS message, split strings into multiple messages after n

  const [loading, setLoading] = useState<boolean>(true);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [fixedSelection, setFixedSelection] = useState<FixedSelection | null>(null);
  const [sendMode, setSendMode] = useState<boolean>(false);
  const [groupMessageSmsResponse, setGroupMessageSmsResponse] = useState<GroupMessageSmsResponse>();
  const [bodyText, setBodyText] = useState<string>('');

  const requiredMessage = t('common:validationMessages.required');

  const GroupMessageSmsPutSchema: Schema<GroupMessageSmsRequestPut> = yup.object().shape({
    id: yup.number().required(),
    name: yup.string().required(requiredMessage),
    product_id: yup.number().optional(),
    selection_id: yup.number().required(requiredMessage),
    selectionName: yup.string().optional().nullable(),
    recipients: yup
      .string()
      .trim()
      .matches(RegexHelper.mobilePhoneNumbers, {
        message: t('common:validationMessages.multiplePhoneNumbers'),
        excludeEmptyString: true,
      }),
    body: yup.string().required(requiredMessage),
    sendDate: yup.string().optional().nullable(),
    sender: yup.string().optional(),
    localAss_id: yup.number().optional().nullable(),
    countyAss_id: yup.number().optional().nullable(),
    senderTitle: yup.string().optional(),
    selectionType: yup.number().optional(),
    speedFormValues: yup.string().optional(),
    speedFormText: yup.string().optional(),
    send: yup.boolean().optional(),
  });

  const formMethods = useForm<GroupMessageSmsRequestPut>({
    resolver: yupResolver(GroupMessageSmsPutSchema),
    mode: 'all',
    defaultValues: {
      name: '',
      sender: '',
      senderTitle: '',
      body: '',
      selection_id: 0,
      send: false,
      recipients: '',
      speedFormText: '',
      speedFormValues: '',
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
    reset,
  } = formMethods;

  useEffect(() => {
    const fetchData = async () => {
      const result = await getGroupMessageSmsById(params.id);
      setLoading(false);
      reset(result.data);
      setGroupMessageSmsResponse(result.data);
      if (result.data.speedFormValues) {
        setFixedSelection({ name: result.data.speedFormText, value: result.data.speedFormValues } as FixedSelection);
      }
      try {
        if (result.data.selection_id !== 0) {
          const sel = await getSelectionById(result.data.selection_id);
          setSelection(sel.data as Selection);
          // ConsoleHelper.log(result.data.selection_id, sel.data);
        }
      } catch (e) {
        ConsoleHelper.error('getSelectionById error:', e);
      }
    };
    if (params.id) {
      fetchData();
    }
  }, [params.id, reset]);

  const goToOverview = useCallback(() => {
    navigate(RoutesEnum.GroupMessageSmsOverview);
  }, [navigate]);

  const postData = useCallback(
    async (data) => {
      ConsoleHelper.log('postData', data);
      try {
        await updateGroupMessageSms(data);
        if (data.send) toast.success(<>{t('groupMessage:sms.edit.sendSuccess')}</>);
        else toast.success(<>{t('groupMessage:sms.edit.saveSuccess')}</>);
        goToOverview();
      } catch (e) {
        if (data.send) toast.warning(<>{t('groupMessage:sms.edit.sendError')}</>);
        else toast.warning(<>{t('groupMessage:sms.edit.saveError')}</>);
        ConsoleHelper.log('saveFormDataError:', e);
      }
    },
    [goToOverview, t],
  );

  const saveFormData = useCallback(
    async (data: GroupMessageSmsRequestPut) => {
      data.speedFormText = data.speedFormText ?? '.';
      data.speedFormValues = data.speedFormValues ?? '.';
      if (selection) data.selectionName = selection?.selectionName;
      data.send = sendMode;
      data.body = bodyText;
      postData(data);
    },
    [bodyText, postData, selection, sendMode],
  );

  const onSubmit = async (data: GroupMessageSmsRequestPut) => {
    await trigger();
    if (Object.keys(errors).length === 0) {
      try {
        await saveFormData({ ...data });
      } catch (e) {
        toast.error('Misslyckades');
      }
    }
  };

  const textEditorInitalized = useCallback(() => {
    return null;
  }, []);

  const returnToOverview = () => {
    navigate(RoutesEnum.GroupMessageSmsOverview);
  };

  const messageIsSent = !!(groupMessageSmsResponse && !!groupMessageSmsResponse?.sendDate);

  return (
    <FormLoaderWrapper loading={loading}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <GridWrapper fixed>
            <Grid item xs={12}>
              <MainContentHeader title={t('groupMessage:sms.edit.header')} />
            </Grid>
            {messageIsSent && groupMessageSmsResponse && (
              <GroupMessageSmsStatusMessage groupMessageSmsResponse={groupMessageSmsResponse} callback={() => ConsoleHelper.log('test')} />
            )}
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 3,
                }}
              >
                <GridWrapper>
                  <Grid item xs={12} md={7}>
                    <GridWrapper>
                      <Grid item xs={12}>
                        <SelectionForm
                          smsMode
                          disabled={messageIsSent}
                          selectionFromServer={selection}
                          fixedSelectionFromServer={fixedSelection}
                          onSelection={(x) => setSelection(x)}
                          onFixedSelection={(x) => setFixedSelection(x)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextField
                          name='name'
                          required
                          label={t('groupMessage:sms.formFields.subject')}
                          inputProps={{ maxLength: 50 }} // TODO
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled={messageIsSent}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextEditor
                          name='body'
                          smsMode
                          initCallback={textEditorInitalized}
                          onChangeCallback={(val) => setBodyText(val)}
                          disabled={messageIsSent}
                        />
                        <GroupMessageSmsMessageLength message={bodyText} splitMessageLength={splitMessageLength} />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextField
                          name='recipients'
                          label={t('groupMessage:formFields.recipients')}
                          helperText={t('groupMessage:formFields.recipients_helperTextSMS')}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled={messageIsSent}
                        />
                      </Grid>
                    </GridWrapper>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <GroupMessageSmsPreview message={bodyText} splitMessageLength={splitMessageLength} disabled={messageIsSent} />
                  </Grid>
                  <Grid item xs={12} textAlign='right' sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Stack direction='row' spacing={1}>
                      <Button variant='outlined' type='button' onClick={returnToOverview}>
                        {t('common:button.cancel')}
                      </Button>
                      <LoadingButton
                        loading={isSubmitting}
                        variant='contained'
                        type='submit'
                        disableElevation
                        onClick={() => setSendMode(false)}
                        disabled={Object.keys(errors).length > 0 || !!groupMessageSmsResponse?.sendDate}
                      >
                        {t('common:button.save')}
                      </LoadingButton>
                      <Divider orientation='vertical' variant='middle' flexItem />
                      <LoadingButton
                        loading={isSubmitting}
                        variant='contained'
                        type='submit'
                        disableElevation
                        disabled={Object.keys(errors).length > 0 || !!groupMessageSmsResponse?.sendDate || (!selection && !fixedSelection)}
                        onClick={() => setSendMode(true)}
                        endIcon={<SendIcon aria-hidden />}
                      >
                        {t('common:button.saveAndSend')}
                      </LoadingButton>
                    </Stack>
                  </Grid>
                </GridWrapper>
              </Paper>
            </Grid>
          </GridWrapper>
        </form>
      </FormProvider>
    </FormLoaderWrapper>
  );
};

export default GroupMessageSmsEditPage;
